/**
 * Change batches and Undo (CS r5b deliverable 163; DECISIONS 212 items 4 and 5): a batch is one
 * `change_batch` row and the `change_log` rows carrying its id, one write the GM made. 2.L's
 * `undo.batches.list` lists them, newest first; `undo.apply` reverts one, writing a new batch of
 * origin `undo` that names it (`revertsId`), or answers 409 `undo_refused`.
 */
import { ChangeOrigin } from '../domain/enums.js';
import { z } from '../zod.js';
import { IsoTimestamp, Uuid } from './common.js';
import { Cursor } from './list.js';
import { queryInt } from './query.js';
/** A route id in the dotted grammar (`ROUTE_ID_PATTERN`), as `change_batch.kind` holds it. */
const BatchKind = z.string().regex(/^[a-z][a-z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/);
/**
 * One batch: its route, origin and session; the batch it reverts (an undo's); the undo that
 * reverted it, if any; how many `change_log` rows it holds.
 */
export const ChangeBatchV1 = z.strictObject({
    id: Uuid,
    kind: BatchKind,
    origin: ChangeOrigin,
    sessionId: Uuid.nullable(),
    revertsId: Uuid.nullable(),
    undoneById: Uuid.nullable(),
    rowCount: z.number().int().min(0),
    createdAt: IsoTimestamp,
});
/** `undo.batches.list`'s query: a session's or a route's batches, a page at a time. */
export const UndoBatchesQueryV1 = z.strictObject({
    sessionId: Uuid.optional(),
    kind: BatchKind.optional(),
    cursor: Cursor.optional(),
    limit: queryInt(1, 200).default(50),
});
/** `undo.apply`'s answer: the batch undone (with `undoneById`) and the undo's own batch. */
export const UndoResultV1 = z.strictObject({
    batch: ChangeBatchV1,
    undoBatch: ChangeBatchV1,
});
/**
 * Why `undo.apply` refused (409 `undo_refused`): a touched row changed since the batch
 * (`row_changed`), or the batch cannot be undone at all (`not_undoable`: an undo, an empty batch,
 * a table no compensator covers; 2.L deliverable 1).
 */
export const UNDO_REFUSED_REASONS = ['row_changed', 'not_undoable'];
export const UndoRefusedReason = z.enum(UNDO_REFUSED_REASONS);
/** The most rows an `undo_refused` answer names. */
export const UNDO_REFUSED_ROWS_MAX = 50;
/** `details` of 409 `undo_refused`: the reason and the rows it names, by table then id. */
export const UndoRefusedDetailsV1 = z.strictObject({
    reason: UndoRefusedReason,
    rows: z
        .array(z.strictObject({
        table: z.string().regex(/^[a-z][a-z0-9_]*$/),
        rowId: z.string().min(1).max(500),
    }))
        .max(UNDO_REFUSED_ROWS_MAX),
});
//# sourceMappingURL=undo.js.map